import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Fab } from "@mui/material";
import { CheckCircleOutline, ArrowUpward } from "@mui/icons-material";

import { CardLayout } from "../v3";
import { getDateAndTime } from "../../utils/formatters";
import { AdminService, ProjectService, ToolService } from "../../services";

export const ToolsActivity = ({ companyDetails = {}, reloadData }) => {
  const [toolActivity, setToolActivity] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageSize: 10,
  });
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const { id } = useParams();
  const observer = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    setToolActivity([]);
    setPaginationState({ page: 1, pageSize: 10 });
    setHasMore(true);
  }, [id, reloadData]);

  useEffect(() => {
    if (hasMore) {
      fetchData(paginationState.page, paginationState.pageSize);
    }
  }, [paginationState]);

  const fetchData = async (page, limit) => {
    if (loading) return;
    try {
      setLoading(true);
      const { data: activityResponse } = await ToolService.getToolActivity(id, {
        page,
        limit,
        sortBy: "createdAt",
        sortOrder: "DESC",
      });

      if (!activityResponse?.rows?.length) {
        setHasMore(false);
        return;
      }

      const storeIds = activityResponse?.rows?.flatMap((tool) => [tool.fromStoreId, tool.toStoreId]);
      const userIds = activityResponse?.rows?.map((tool) => tool?.movedById);

      if (storeIds?.length > 0) {
        const { data: projects } = await ProjectService.getProjectList({
          storeIds: storeIds.join(","),
          limit: storeIds.length,
        });
        setProjects((prev) => [...prev, ...projects.rows]);
      }

      if (userIds?.length > 0) {
        const { data: users } = await AdminService.getUserList({
          ids: userIds.join(","),
          limit: userIds.length,
        });
        setUsers((prev) => [...prev, ...users.rows]);
      }

      setToolActivity((prev) => [...prev, ...activityResponse.rows]);
      setTotalCount(activityResponse.count);

      if (activityResponse.rows.length < limit) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("🚀 ~ fetchData ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Intersection Observer callback
  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPaginationState((prev) => ({
            ...prev,
            page: prev.page + 1,
          }));
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore],
  );

  const getStoreName = (fromStoreId, toStoreId) => {
    if (fromStoreId === null) {
      if (toStoreId === companyDetails.storeId) return `Tool added to the company warehouse (${companyDetails.name})`;
      else {
        const project = projects.find((project) => project.storeId === toStoreId);
        if (project) return `Tool added to ${project.name}`;
      }
    }

    if (fromStoreId && toStoreId) {
      if (fromStoreId === companyDetails?.storeId) {
        const toProject = projects.find((project) => project.storeId === toStoreId);
        if (toProject) return `Tool moved from the company warehouse (${companyDetails.name}) to ${toProject.name}`;
      } else if (toStoreId === companyDetails?.storeId) {
        const fromProject = projects.find((project) => project.storeId === fromStoreId);
        if (fromProject) return `Tool returned from ${fromProject.name} to the company warehouse (${companyDetails.name})`;
      }
      const toProject = projects.find((project) => project.storeId === toStoreId);
      const fromProject = projects.find((project) => project.storeId === fromStoreId);
      if (fromProject && toProject) return `Tool moved from ${fromProject.name} to ${toProject.name}`;
    }

    if (toStoreId === null) {
      if (fromStoreId === companyDetails.storeId) return `Tool removed from the company warehouse (${companyDetails.name})`;
      else {
        const project = projects.find((project) => project.storeId === fromStoreId);
        if (project) return `Tool removed from ${project.name}`;
      }
    }
  };

  const getUserName = (movedById) => {
    const user = users.find((user) => user.id === movedById);
    return user ? `${user.firstName} ${user.lastName}` : "Unknown User";
  };

  // Track scroll to show button
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      setShowScrollTop(scrollContainerRef.current.scrollTop > 300);
    }
  };

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <CardLayout>
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          minHeight: { xs: "auto", md: "60vh" },
          maxHeight: "60vh",
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": {
            background: "#f0f0f0",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c0c0c0",
            borderRadius: "4px",
            border: "2px solid #f0f0f0",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#a0a0a0",
          },
          position: "relative",
        }}
      >
        {toolActivity.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
            No activity found.
          </Typography>
        ) : (
          <Box sx={{ pr: 1 }}>
            {toolActivity.map((item, index) => {
              const isLast = index === toolActivity.length - 1;
              const showLine = toolActivity.length > 1 || isLast;
              return (
                <Box
                  key={item.id}
                  ref={isLast ? lastItemRef : null} // attach observer to last item
                  display="flex"
                  position="relative"
                  mb={2}
                >
                  {/* Timeline indicator */}
                  <Box sx={{ position: "relative", mr: 2 }}>
                    <Box
                      sx={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#fff",
                        border: "2px solid #e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                      }}
                    >
                      <CheckCircleOutline fontSize="small" color="action" />
                    </Box>
                    {/* Line below the circle */}
                    {showLine && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "20px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "2px",
                          height: toolActivity.length > 1 && !isLast ? "100%" : "30px",
                          backgroundColor: toolActivity.length > 1 && !isLast ? "#e0e0e0" : "transparent",
                          borderLeft: toolActivity.length > 1 && !isLast ? "none" : "2px dotted #e0e0e0",
                        }}
                      />
                    )}
                  </Box>
                  {/* Content */}
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      {getStoreName(item.fromStoreId, item.toStoreId)}
                    </Typography>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Moved By: {getUserName(item.movedById)}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        Quantity: {item.movedQuantity}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {getDateAndTime(item.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {loading && (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Loading...
              </Typography>
            )}
            {!hasMore && (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {totalCount} Tool movements
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Scroll-to-top button */}
      {showScrollTop && (
        <Fab
          size="small"
          onClick={scrollToTop}
          sx={{
            position: "absolute",
            bottom: 48,
            right: 62,
            zIndex: 10,
            backgroundColor: "#fff",
            color: "#674BF5",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <ArrowUpward />
        </Fab>
      )}
    </CardLayout>
  );
};
